import { bgBlue, bgGreen, bgRed, bgYellow, brandColor, dim, gray, hidden, white } from "./colors.mjs";
import { stderr, stdout } from "./streams.mjs";
import { checkMacOSVersion } from "./check-macos-version.mjs";
import { showCursor } from "./cursor.mjs";
import { exit } from "node:process";

//#region index.ts
const shapes = {
	diamond: "◇",
	dash: "─",
	radioInactive: "○",
	radioActive: "●",
	backActive: "◀",
	backInactive: "◁",
	bar: "│",
	leftT: "├",
	rigthT: "┤",
	arrows: {
		left: "‹",
		right: "›"
	},
	corners: {
		tl: "╭",
		bl: "╰",
		tr: "╮",
		br: "╯"
	}
};
const status = {
	error: bgRed(` ERROR `),
	warning: bgYellow(` WARNING `),
	info: bgBlue(` INFO `),
	success: bgGreen(` SUCCESS `),
	cancel: white.bgRed(` X `)
};
const space = (n = 1) => {
	return hidden(" ".repeat(n));
};
const LOGGER_LEVELS = {
	none: -1,
	error: 0,
	warn: 1,
	info: 2,
	log: 3,
	debug: 4
};
let currentLogLevel = "log";
function setLogLevel(level) {
	currentLogLevel = level;
}
function getLogLevel() {
	return currentLogLevel;
}
const logRaw = (msg) => {
	if (LOGGER_LEVELS[getLogLevel()] >= LOGGER_LEVELS.log) stdout.write(`${msg}\n`);
};
const log = (msg) => {
	logRaw(msg.split("\n").map((ln) => `${gray(shapes.bar)}${ln.length > 0 ? " " + white(ln) : ""}`).join("\n"));
};
const newline = () => {
	log("");
};
const format = (msg, { linePrefix = gray(shapes.bar), firstLinePrefix = linePrefix, newlineBefore = false, newlineAfter = false, formatLine = (line) => white(line), multiline = true } = {}) => {
	const formattedLines = (multiline ? msg.split("\n") : [msg]).map((line, i) => (i === 0 ? firstLinePrefix : linePrefix) + space() + formatLine(line));
	if (newlineBefore) formattedLines.unshift(linePrefix);
	if (newlineAfter) formattedLines.push(linePrefix);
	return formattedLines.join("\n");
};
const updateStatus = (msg, printNewLine = true) => {
	logRaw(format(msg, {
		firstLinePrefix: gray(shapes.leftT),
		linePrefix: gray(shapes.bar),
		newlineAfter: printNewLine
	}));
};
const startSection = (heading, subheading, printNewLine = true) => {
	logRaw(`${gray(shapes.corners.tl)} ${brandColor(heading)} ${subheading ? dim(subheading) : ""}`);
	if (printNewLine) newline();
};
const endSection = (heading, subheading) => {
	logRaw(`${gray(shapes.corners.bl)} ${brandColor(heading)} ${subheading ? dim(subheading) : ""}\n`);
};
const cancel = (msg, { shape = shapes.corners.bl, multiline = false } = {}) => {
	logRaw(format(msg, {
		firstLinePrefix: `${gray(shape)} ${status.cancel}`,
		linePrefix: gray(shapes.bar),
		newlineBefore: true,
		formatLine: (line) => dim(line),
		multiline
	}));
};
const warn = (msg, { shape = shapes.corners.bl, multiline = false, newlineBefore = true } = {}) => {
	logRaw(format(msg, {
		firstLinePrefix: gray(shape) + space() + status.warning,
		linePrefix: gray(shapes.bar),
		formatLine: (line) => dim(line),
		multiline,
		newlineBefore
	}));
};
const success = (msg, { shape = shapes.corners.bl, multiline = false } = {}) => {
	logRaw(format(msg, {
		firstLinePrefix: gray(shape) + space() + status.success,
		linePrefix: gray(shapes.bar),
		newlineBefore: true,
		formatLine: (line) => dim(line),
		multiline
	}));
};
const stripAnsi = (str) => {
	const pattern = ["[\\u001B\\u009B][[\\]()#;?]*(?:(?:(?:(?:;[-a-zA-Z\\d\\/#&.:=?%@~_]+)*|[a-zA-Z\\d]+(?:;[-a-zA-Z\\d\\/#&.:=?%@~_]*)*)?\\u0007)", "(?:(?:\\d{1,4}(?:;\\d{0,4})*)?[\\dA-PR-TZcf-nq-uy=><~]))"].join("|");
	const regex = RegExp(pattern, "g");
	return str.replace(linkRegex, "$2").replace(regex, "");
};
const linkRegex = /\u001B\]8;;(?<url>.+)\u001B\\(?<label>.+)\u001B\]8;;\u001B\\/g;
const hyperlink = (url, label = url) => {
	return `\u001B]8;;${url}\u001B\\${label}\u001B]8;;\u001B\\`;
};
const crash = (msg, extra) => {
	error(msg, extra);
	exit(1);
};
const error = (msg, extra, corner = shapes.corners.bl) => {
	const currentLevel = getLogLevel();
	if (msg && LOGGER_LEVELS[currentLevel] >= LOGGER_LEVELS.error) stderr.write(`${gray(corner)} ${status.error} ${dim(msg)}\n${extra ? space() + extra + "\n" : ""}`);
};

//#endregion
export { cancel, checkMacOSVersion, crash, endSection, error, format, getLogLevel, hyperlink, linkRegex, log, logRaw, newline, setLogLevel, shapes, showCursor, space, startSection, status, stripAnsi, success, updateStatus, warn };